/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';
import get from 'lodash/get';
import { ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import UserDatabases from '@Services/UserDatabases';
import { useDispatch, useSelector } from 'react-redux';
import { ValidatedTextInput } from '@els/els-react--text-input';
import { deleteUserFormAction, saveUserFormAction } from '../../QueryletActions';
import { forms } from '../../QuerySelector';

import './FormDragNode.scss';

function FormDragNode({ node, addToBuilder }) {
  const dispatch = useDispatch();
  const [isEdit, setEdit] = useState(false);
  const [displayName, setDisplayName] = useState(node.displayName);
  const formList = useSelector(forms);
  const [{ isDragging }, dragRef] = useDrag(
    {
      type: 'DragNode',
      item: { node, type: 'DragNode' },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    },
    [node]
  );

  useEffect(() => {
    setDisplayName(node.displayName);
  }, [node]);

  useEffect(() => {
    if (isDragging) document.body.style.cursor = 'move !important';
    else document.body.style.cursor = 'normal';
  }, [isDragging]);

  const isFormNameUnique = () => {
    const myFormsList = formList.filter((formItem) => formItem.displayName === 'My Forms')[0];

    return (
      get(myFormsList, 'children', []).filter((child) => child.displayName === displayName && child.formId !== node.formId).length === 0
    );
  };

  const editUserFormName = () => {
    const alphanumeric = /^[a-zA-Z0-9 ]+$/;

    setEdit(!isEdit);
    if (
      !displayName ||
      displayName.trim().length < 3 ||
      displayName.trim().length > 20 ||
      !alphanumeric.test(displayName.trim()) ||
      !isFormNameUnique()
    ) {
      setEdit(true);
    } else {
      dispatch(saveUserFormAction(displayName, node));
    }
  };

  const onDeleteClick = () => {
    setEdit(false);
    dispatch(deleteUserFormAction(node));
  };

  return (
    <div className="query-builder-search-section__drag-node" key={`form-${node.uuid}`}>
      <div
        className="query-builder-search-section__drag-node-content"
        onClick={(e) => e.currentTarget === e.target && addToBuilder(node)}
        onKeyDown={(e) => e.currentTarget === e.target && addToBuilder(node)}>
        {isEdit && (
          <div>
            <ValidatedTextInput
              id={`editName-${node.id}`}
              className={isFormNameUnique() ? '' : 'c-els-field--error'}
              value={displayName}
              label={displayName}
              min={3}
              max={20}
              shouldHideLabel
              required
              style={{ maxWidth: '9rem' }}
              onChange={(event) => setDisplayName(event.target.value)}
            />
            {!isFormNameUnique() && (
              <div className="c-els-field__message c-els-field__message--error">
                <span className="c-els-field__message-icon icon__wrapper u-els-color-warn">Please enter a unique form name</span>
              </div>
            )}
          </div>
        )}
        {!isEdit && (
          <ButtonWithIcon
            type="link"
            sprite="Layers"
            linkIconSize="s"
            className={node.type === 'form' ? 'group-name' : ''}
            onClick={(e) => addToBuilder(node) && e.stopPropagation()}>
            {node.displayName}
          </ButtonWithIcon>
        )}
        <div style={{ display: 'flex' }}>
          {node.databases && node.databases.length > 1 && (
            <Flyout
              flyout={
                <>
                  <h4>Databases</h4>
                  <div className="query-builder-search-section__drag-node-db">
                    {node.databases.map((option) => (
                      <ButtonWithIcon key={option} type="link" sprite="Database" onClick={(event) => event.stopPropagation()}>
                        {UserDatabases.getDbName(option)}
                      </ButtonWithIcon>
                    ))}
                  </div>
                </>
              }
              shouldCloseOnESC>
              <ButtonWithIcon
                type="link"
                size="small"
                linkIconSize="s"
                iconOnly
                sprite="Database"
                onClick={(event) => event.stopPropagation()}>
                {node.databases[0]}
              </ButtonWithIcon>
            </Flyout>
          )}
          {node.editMode && (
            <>
              <Flyout
                trigger="hover"
                className="qb-drag-node-icon"
                flyout={isEdit ? 'Save' : 'Edit group name'}
                placement="bottom"
                theme="simple">
                <ButtonWithIcon
                  id={`edit-${node.id}`}
                  iconOnly
                  type="link"
                  linkIconSize="s"
                  size="small"
                  sprite={isEdit ? 'Checkmark' : 'Edit'}
                  onClick={() => editUserFormName()}>
                  {isEdit ? 'Save Edited Form' : 'Edit Form'}
                </ButtonWithIcon>
              </Flyout>
              <Flyout trigger="hover" className="qb-drag-node-icon" flyout="Delete form" placement="bottom" theme="simple">
                <ButtonWithIcon
                  id={`delete-${node.id}`}
                  iconOnly
                  type="link"
                  linkIconSize="s"
                  size="small"
                  style={{ padding: '0 .5rem' }}
                  sprite="Trash"
                  onClick={() => onDeleteClick()}>
                  Delete Form
                </ButtonWithIcon>
              </Flyout>
            </>
          )}
          <div ref={dragRef} aria-live="polite">
            {isDragging && <span className="sr-only">{node.displayName} is dragging to querybuilder area</span>}
            <Flyout trigger="hover" flyout={<span aria-hidden="true">Drag form</span>} placement="bottom" theme="simple">
              <ButtonWithIcon
                id={`querybuilder-dragform-${node.id}`}
                ref={dragRef}
                draggable
                iconOnly
                className="cursor-move"
                onClick={(dragE) => dragE.stopPropagation()}
                linkIconSize="xxs"
                sprite="Drag"
                type="borderless"
                tabIndex="-1"
                aria-grabbed={isDragging}>
                drag
              </ButtonWithIcon>
            </Flyout>
          </div>
        </div>
      </div>
    </div>
  );
}

FormDragNode.propTypes = {
  node: PropTypes.object,
  addToBuilder: PropTypes.func,
};

export default FormDragNode;
